import { configureStore, combineReducers } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import sessionReducer from "./slices/sessionSlice";
import chatReducer, { syncWebSocketMessage } from "./slices/chatSlice";
import websocketReducer from "./slices/websocketSlice";
import documentsReducer, { documentGenerated } from "./slices/documentsSlice";
import chat8StabilizationMiddlewareFn from "./middleware/chat8StabilizationMiddleware";
import type { AppState } from "@/types";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["session", "chat", "documents"], // Adiciona 'documents' para persistência
};

const rootReducer = combineReducers({
  session: sessionReducer,
  chat: chatReducer,
  websocket: websocketReducer,
  documents: documentsReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

/**
 * @critical MIDDLEWARE DE SINCRONIZAÇÃO WEBSOCKET-CHAT
 * @description Middleware CRÍTICO que sincroniza mensagens do WebSocket com o chat
 *
 * @flow CRÍTICO:
 * 1. WebSocket recebe mensagem → ChatConsumer processa
 * 2. setMessage/addMessage (websocketSlice) → Middleware detecta
 * 3. syncWebSocketMessage (chatSlice) → Estado atualizado
 * 4. Componentes re-renderizam → Painel de perguntas atualiza
 *
 * @warning NÃO REMOVER: Este middleware é OBRIGATÓRIO para sincronização em tempo real
 * @warning NÃO MODIFICAR: Alterações podem quebrar a sincronização WebSocket-Chat
 * @warning NÃO SUBSTITUIR: Outros middlewares não podem substituir esta funcionalidade
 *
 * @dependencies
 * - syncWebSocketMessage (chatSlice) - Função de sincronização
 * - websocketSlice - Estado das mensagens WebSocket
 * - chatSlice - Estado das mensagens do chat
 *
 * @actionTypes Monitored:
 * - 'websocket/setMessage' - Mensagem definida no WebSocket
 * - 'websocket/addMessage' - Mensagem adicionada ao WebSocket
 *
 * @validation: Verifica se payload tem content antes de sincronizar
 * @performance: Executa apenas quando necessário, sem reprocessamento
 * @deduplication: Evita processamento duplicado da mesma mensagem
 */
export const websocketSyncMiddleware = (store: any) => (next: any) => (action: any) => {
  const result = next(action);

  // Verificar se é uma ação do WebSocket que contém mensagem
  if (action.type === 'websocket/setMessage' || action.type === 'websocket/addMessage') {
    const payload = action.payload;

    // Validação melhorada - verificar se há conteúdo válido
    const isConnectionMessage = payload?.type === 'connection_established';
    const isPingPong = payload?.type === 'ping' || payload?.type === 'pong';
    const isHeartbeat = payload?.type === 'heartbeat';
    const isDocumentGenerated = payload?.type === 'document_generated';

    // Handle document_generated messages separately - dispatch to documents slice
    if (isDocumentGenerated && payload?.generated_doc_id) {
      try {
        store.dispatch(documentGenerated({
          generated_doc_id: payload.generated_doc_id,
          status: payload.status || 'completed',
          download_url: payload.download_url || null,
          error_message: payload.error_message || null,
          updated_at: new Date().toISOString(),
        }));
        console.info('[websocketSyncMiddleware] 📄 Document generated event processed:', payload.generated_doc_id);
      } catch (error) {
        console.error('[websocketSyncMiddleware] ❌ Error processing document_generated:', error);
      }
      return result;
    }

    const hasValidContent = payload && (
      (payload.content && payload.content.trim() !== '') ||
      (payload.message && payload.message.trim() !== '')
    );

    // Só sincronizar se for mensagem de conexão OU tiver conteúdo válido (exceto ping/pong/heartbeat)
    const shouldSync = (isConnectionMessage || hasValidContent) && !isPingPong && !isHeartbeat;



    // Validar se o payload deve ser sincronizado
    if (shouldSync) {
      // Verificar se a mensagem já foi processada (deduplicação melhorada)
      const currentState = store.getState();
      const messageId = payload?.id;

      // Permitir mensagens de conexão mesmo que já existam (para reconexão)
      if (messageId && !isConnectionMessage) {
        // Verificar se a mensagem já existe no chat
        const messageAlreadyExists = currentState.chat.messages.some(
          (msg: any) => msg.id === messageId
        );
        if (messageAlreadyExists) {
          return result;
        }
      }


      if (typeof store.dispatch === 'function') {
        try {
          // Normalizar payload para UnifiedMessage (com validação de conteúdo)
          const messageContent = payload.content || payload.message || '';

          // Para mensagens de conexão, permitir conteúdo vazio (será tratado no chatSlice)
          if (!isConnectionMessage && (!messageContent || messageContent.trim() === '')) {
            console.warn('[websocketSyncMiddleware] ⚠️ Mensagem com conteúdo vazio ignorada:', {
              payload,
              reason: 'Empty content detected'
            });
            return result;
          }

          const normalizedPayload = {
            id: payload.id || `ws_${Date.now()}_${Math.random()}`,
            type: payload.type || 'message',
            content: messageContent, // Usar conteúdo original sem fallback vazio
            role: payload.role || (payload.sender?.type === 'user' ? 'user' : 'assistant'),
            timestamp: payload.timestamp || Date.now(),
            status: payload.status || 'sent',
            session_id: payload.session_id || '',
            document_id: payload.document_id || '',
            sender: payload.sender || {
              id: 'assistant',
              type: 'assistant',
              name: 'Assistente',
            },
            created_at: payload.created_at || new Date().toISOString(),
            updated_at: payload.updated_at || new Date().toISOString(),
            metadata: payload.metadata || {},
          };
          store.dispatch(syncWebSocketMessage(normalizedPayload));

          // Verificação pós-sincronização melhorada - apenas para mensagens não-sistema
          if (!isConnectionMessage && !isPingPong && !isHeartbeat) {
            setTimeout(() => {
              const currentState = store.getState();
              const websocketCount = currentState.websocket.messages.length;
              const chatCount = currentState.chat.messages.length;
              const lastWebSocketMessage = currentState.websocket.messages[websocketCount - 1];
              const lastChatMessage = currentState.chat.messages[chatCount - 1];



              // Se há inconsistência, tentar corrigir apenas para mensagens com conteúdo
              if (chatCount < websocketCount) {
                // Forçar sincronização da última mensagem do WebSocket
                if (lastWebSocketMessage && !currentState.chat.messages.some((msg: any) => msg.id === lastWebSocketMessage.id)) {
                  const forcedContent = lastWebSocketMessage.content || lastWebSocketMessage.message || '';
                  if (forcedContent && forcedContent.trim() !== '') {
                    store.dispatch(syncWebSocketMessage(lastWebSocketMessage));
                  }
                }
              }
            }, 100);
          }
        } catch (error) {
          console.error('[websocketSyncMiddleware] ❌ Erro ao sincronizar mensagem:', error);
          setTimeout(() => {
            try {
              const messageContent = payload.content || payload.message || '';

              // Não tentar retry se o conteúdo estiver vazio (exceto conexão)
              if (!isConnectionMessage && (!messageContent || messageContent.trim() === '')) {
                console.warn('[websocketSyncMiddleware] ⚠️ Retry ignorado - mensagem com conteúdo vazio');
                return;
              }

              const normalizedPayload = {
                id: payload.id || `ws_${Date.now()}_${Math.random()}`,
                type: payload.type || 'message',
                content: messageContent,
                role: payload.role || (payload.sender?.type === 'user' ? 'user' : 'assistant'),
                timestamp: payload.timestamp || Date.now(),
                status: payload.status || 'sent',
                session_id: payload.session_id || '',
                document_id: payload.document_id || '',
                sender: payload.sender || {
                  id: 'assistant',
                  type: 'assistant',
                  name: 'Assistente',
                },
                created_at: payload.created_at || new Date().toISOString(),
                updated_at: payload.updated_at || new Date().toISOString(),
                metadata: payload.metadata || {},
              };
              store.dispatch(syncWebSocketMessage(normalizedPayload));

            } catch (retryError) {
              console.error('[websocketSyncMiddleware] ❌ Retry sincronização falhou:', retryError);
            }
          }, 500);
        }
      } else {
        console.error('[websocketSyncMiddleware] ❌ store.dispatch não é uma função');
      }
    } else {
      console.warn('[websocketSyncMiddleware] ⚠️ Payload inválido, ignorando sincronização:', {
        payload,
        reason: 'No valid content found and not a connection message',
        isConnectionMessage,
        isPingPong,
        isHeartbeat,
        contentExists: !!(payload?.content || payload?.message),
        contentValid: hasValidContent,
        payloadType: typeof payload,
      });
    }
  }

  return result;
};

/**
 * @critical CONFIGURAÇÃO DO STORE COM MIDDLEWARE
 * @description Store configurado com middleware de sincronização WebSocket
 *
 * @warning NÃO REMOVER: websocketSyncMiddleware da configuração
 * @warning NÃO MODIFICAR: Ordem dos middlewares pode afetar sincronização
 * @warning NÃO SUBSTITUIR: Outros middlewares não podem substituir websocketSyncMiddleware
 *
 * @middleware Order:
 * 1. Default middleware (serializableCheck, etc.)
 * 2. websocketSyncMiddleware (CRÍTICO para sincronização)
 *
 * @serializableCheck: Ignora ações de persistência para evitar warnings
 */
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(websocketSyncMiddleware)
      .concat(chat8StabilizationMiddlewareFn),
});

/**
 * @critical PERSISTÊNCIA CONFIGURADA
 * @description Configuração de persistência para manter estado entre sessões
 *
 * @whitelist: Slice que devem ser persistidos
 * - session: Sessões de chat
 * - chat: Mensagens e estado do chat
 * - documents: Documentos selecionados
 *
 * @note: websocket não é persistido (dados temporários)
 */
const persistor = persistStore(store);

export { store, persistor };
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.store = store;
}
export default store;

export type RootState = AppState;
export type AppDispatch = typeof store.dispatch;
